import { uploadCommendaAiAttachment } from '@commenda/api/commenda-ai/api';
import { COMMENDA_AI_MESSAGE_MAX_LENGTH } from '@commenda/api/commenda-ai/constants';
import type { IDocumentReaderUploadReference } from '@commenda/api/commenda-ai/document-reader/types';
import { useDocumentReaderUploadLimits } from '@commenda/api/commenda-ai/document-reader/useDocumentReaderUploadLimits';
import type { CommendaAiUploadFile } from '@commenda/api/commenda-ai/types';
import { FeatureLaunch, isFeatureLaunched } from '@commenda/feature-launches';
import { Dropdown, Icons, toast } from '@commenda/kakashi';
import type { AgentAttachment } from '@commenda/types';
import { cn } from '@commenda/utils';
import type { ChatStatus } from 'ai';
import { ArrowUpIcon, SquareIcon } from 'lucide-react';
import {
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type ChangeEvent,
  type DOMAttributes,
  type DragEvent,
  type ReactNode,
  type Ref,
} from 'react';
import {
  PromptInput,
  PromptInputBody,
  PromptInputButton,
  PromptInputFooter,
  PromptInputHeader,
  PromptInputProvider,
  PromptInputSubmit,
  PromptInputTextarea,
  PromptInputTools,
  usePromptInputController,
  type PromptInputControllerProps,
  type PromptInputMessage,
} from './ai-elements/prompt-input';
import { AttachmentPillGroups } from './AttachmentPillGroups';
import { ChatUploadHelper } from './ChatUploadHelper';
import { useCommendaAiHost } from './CommendaAiHostProvider';
import { DataRoomUploadHelper } from './data-room/DataRoomUploadHelper';
import { DataRoomUploadProgress } from './data-room/DataRoomUploadProgress';
import { useDataRoomUpload } from './data-room/useDataRoomUpload';
import { useChatFolderUpload } from './useChatFolderUpload';

/** Mirrors the backend's upload allowlist (AttachmentService.ALLOWED_MIME_TYPES). `accept` is only a hint
 * to the file dialog — the presign endpoint is what actually rejects a type — so the two are kept in step
 * to stop the picker offering a file the API refuses, or hiding one it takes. Extensions are listed too,
 * because a machine with no app registered for a type cannot match the file by MIME type. */
const ACCEPT = [
  'image/*',
  'application/pdf',
  'application/msword',
  '.doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.docx',
  'application/rtf',
  '.rtf',
  'text/plain',
  '.txt',
  'text/markdown',
  '.md',
  'text/richtext',
  'text/csv',
  '.csv',
  'application/json',
  '.json',
  'application/vnd.ms-excel',
  '.xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  '.xlsx',
].join(',');

const MESSAGE_LENGTH_HINT_FROM = COMMENDA_AI_MESSAGE_MAX_LENGTH * 0.9;
const MESSAGE_TOO_LONG_ERROR = `Messages can be up to ${COMMENDA_AI_MESSAGE_MAX_LENGTH.toLocaleString()} characters. Attach longer text as a .txt file instead.`;

const MAX_ATTACHMENTS = 25;

interface ComposerAttachment {
  data: AgentAttachment;
  /** Local blob URL for an instant preview (the stored S3 URL is private). */
  previewUrl: string;
}

/** Circular send button: arrow-up when idle, greyed while the composer is empty or an upload is in
 * flight, and the AI SDK stop control while a turn is generating. */
function ChatSubmit({
  status,
  onStop,
  blocked,
}: {
  status: ChatStatus;
  onStop?: () => void;
  blocked: boolean;
}) {
  const { textInput } = usePromptInputController();
  const isGenerating = status === 'submitted' || status === 'streaming';
  const isEmpty = textInput.value.trim().length === 0;
  const isTooLong = textInput.value.length > COMMENDA_AI_MESSAGE_MAX_LENGTH;

  return (
    <PromptInputSubmit
      status={status}
      onStop={onStop}
      disabled={isGenerating ? !onStop : isEmpty || isTooLong || blocked}
      className={cn(
        'size-7 shrink-0 rounded-full',
        'bg-fg-brand text-white hover:bg-fg-brand/90',
        'disabled:bg-fg-disabled disabled:text-white disabled:opacity-100'
      )}
    >
      {isGenerating ? (
        <SquareIcon className="size-4" />
      ) : (
        <ArrowUpIcon className="size-4" />
      )}
    </PromptInputSubmit>
  );
}

/** Shown only near the limit, so ordinary messages keep a clean footer. */
function MessageLengthHint() {
  const { textInput } = usePromptInputController();
  const { length } = textInput.value;
  if (length < MESSAGE_LENGTH_HINT_FROM) return null;
  const isTooLong = length > COMMENDA_AI_MESSAGE_MAX_LENGTH;

  return (
    <p
      aria-live="polite"
      className={cn(
        'txt-extra-small-regular px-xl',
        isTooLong ? 'text-text-error' : 'text-text-tertiary'
      )}
    >
      {isTooLong
        ? MESSAGE_TOO_LONG_ERROR
        : `${length.toLocaleString()} / ${COMMENDA_AI_MESSAGE_MAX_LENGTH.toLocaleString()} characters`}
    </p>
  );
}

function PromptInputControllerBridge({
  controllerRef,
}: {
  controllerRef: Ref<PromptInputControllerProps>;
}) {
  const controller = usePromptInputController();
  useImperativeHandle(controllerRef, () => controller, [controller]);
  return null;
}

/** The chat composer card (rounded, hairline border, soft shadow) with a paperclip attach button
 * and a circular brand-blue send. Attachments upload to S3 immediately (chips appear) and are
 * handed to `onSubmit`
 * with the text. Shared by the new-chat screen and the in-conversation view. */
export function ChatComposer({
  placeholder,
  onSubmit,
  status = 'ready',
  onStop,
  initialInput = '',
  showDisclaimer = true,
  autoFocus = false,
  toolsSlot,
  filesToIngest = null,
  onFilesIngested,
  allowAttachments = true,
  disabled = false,
  contextSlot,
  dataRoomTarget,
}: {
  placeholder: string;
  /** A host may resolve with whatever its create returns; the composer only waits on it. */
  onSubmit: (
    text: string,
    attachments: AgentAttachment[],
    dataRoom?: IDocumentReaderUploadReference
  ) => void | Promise<unknown>;
  status?: ChatStatus;
  onStop?: () => void;
  initialInput?: string;
  /** The "Chat is in beta." caption under the input. Hidden on the blank new-chat screen. */
  showDisclaimer?: boolean;
  /** Focus the textarea on mount so the user can type immediately (e.g. the new-chat screen). */
  autoFocus?: boolean;
  /** Extra controls rendered beside the paperclip. Omitted by every production caller, so the footer is
   * unchanged for them. */
  toolsSlot?: ReactNode;
  /** Files handed down by an outer drop target, so a drop anywhere over the chat lands in the same
   * attachment state as the paperclip. */
  filesToIngest?: CommendaAiUploadFile[] | null;
  /** Called once the handed-down files have been taken, so the parent can clear them. */
  onFilesIngested?: () => void;
  /** False for a surface whose send takes text only, so no file is offered that would be dropped. */
  allowAttachments?: boolean;
  /** No typing and no sending, for a conversation that can no longer take a message. */
  disabled?: boolean;
  /** Context pills that sit on a tray behind the top of the input (Figma Composer group). */
  contextSlot?: ReactNode;
  /** The chat conversation a data room may be uploaded into. Only a host that sends the room's id
   * passes it, and the room mode still needs the backend's upload-limits to answer allowed. */
  dataRoomTarget?: { conversationId: string; companyId: number | null };
}) {
  const { renderConnectorsModal } = useCommendaAiHost();
  // The menu entry is only offered where the host app has a connector directory to open.
  const isConnectorsLaunched =
    isFeatureLaunched(FeatureLaunch.SCHEDULED_AGENTS) &&
    !!renderConnectorsModal;
  const [isConnectorsModalOpen, setIsConnectorsModalOpen] = useState(false);
  const [isAddMenuOpen, setIsAddMenuOpen] = useState(false);
  const [attachments, setAttachments] = useState<ComposerAttachment[]>([]);
  const [uploadingCount, setUploadingCount] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const canUploadFolders = useChatFolderUpload();
  const { uploadLimits } = useDocumentReaderUploadLimits(
    !!dataRoomTarget && allowAttachments
  );
  const dataRoom = useDataRoomUpload({
    uploadLimits,
    conversationId: dataRoomTarget?.conversationId ?? null,
    companyId: dataRoomTarget?.companyId ?? null,
  });
  const dataRoomFileInputRef = useRef<HTMLInputElement>(null);
  const dataRoomFolderInputRef = useRef<HTMLInputElement>(null);

  // Revoke composer preview blob URLs on unmount (submit/remove revoke eagerly).
  const attachmentsRef = useRef(attachments);
  attachmentsRef.current = attachments;
  useEffect(
    () => () => {
      attachmentsRef.current.forEach((item) =>
        URL.revokeObjectURL(item.previewUrl)
      );
    },
    []
  );

  const removeAttachment = (s3Key: string) =>
    setAttachments((prev) => {
      const found = prev.find((item) => item.data.s3Key === s3Key);
      if (found) URL.revokeObjectURL(found.previewUrl);
      return prev.filter((item) => item.data.s3Key !== s3Key);
    });

  const handleFiles = async (incoming: CommendaAiUploadFile[]) => {
    if (incoming.length === 0 || !allowAttachments) return;
    const room = Math.max(
      MAX_ATTACHMENTS - attachments.length - uploadingCount,
      0
    );
    const files = incoming.slice(0, room);
    if (files.length < incoming.length)
      toast.error(
        `A message takes up to ${MAX_ATTACHMENTS} files, so ${incoming.length - files.length} weren't added.`
      );
    if (files.length === 0) return;
    setUploadingCount((count) => count + files.length);
    await Promise.all(
      files.map(async (upload) => {
        try {
          const uploaded = await uploadCommendaAiAttachment(upload);
          if (!uploaded.success || !uploaded.data) {
            toast.error(uploaded.error ?? `Could not upload ${upload.path}.`);
            return;
          }
          const attachment = uploaded.data;
          setAttachments((prev) => [
            ...prev,
            // Local blob URL gives an instant, correct preview (the stored S3 URL is private).
            { data: attachment, previewUrl: URL.createObjectURL(upload.file) },
          ]);
        } finally {
          setUploadingCount((count) => count - 1);
        }
      })
    );
  };

  const ingestFiles = (incoming: CommendaAiUploadFile[]) =>
    dataRoom.isAllowed &&
    DataRoomUploadHelper.isDataRoomSelection(incoming, MAX_ATTACHMENTS)
      ? dataRoom.startUpload(incoming)
      : handleFiles(incoming);

  // Uploading is a side effect, which is what useEffect is for. The ref guard stops the same
  // files being uploaded twice if the parent re-renders before it clears.
  const ingestedFilesRef = useRef<CommendaAiUploadFile[] | null>(null);

  useEffect(() => {
    if (!filesToIngest || filesToIngest === ingestedFilesRef.current) return;

    ingestedFilesRef.current = filesToIngest;
    ingestFiles(filesToIngest)
      .catch(() => undefined)
      .finally(() => onFilesIngested?.());
  }, [filesToIngest, onFilesIngested]);

  const onFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;
    if (files)
      ingestFiles(ChatUploadHelper.fromFileList(files)).catch(() => undefined);
    // Reset (via the refs, not the param) so the same file or folder can be re-selected.
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (folderInputRef.current) folderInputRef.current.value = '';
  };

  const onDataRoomFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;
    if (files)
      dataRoom
        .startUpload(ChatUploadHelper.fromFileList(files))
        .catch(() => undefined);
    if (dataRoomFileInputRef.current) dataRoomFileInputRef.current.value = '';
    if (dataRoomFolderInputRef.current)
      dataRoomFolderInputRef.current.value = '';
  };

  const onComposerDragOver = (event: DragEvent<HTMLDivElement>) => {
    if (!event.dataTransfer.types.includes('Files')) return;
    event.preventDefault();
  };

  const onComposerDrop = (event: DragEvent<HTMLDivElement>) => {
    if (!event.dataTransfer.types.includes('Files')) return;
    event.preventDefault();
    // Capture phase, and stopped here, because `PromptInput` already registers its own bubble-phase drop
    // listener on its <form>. That one pushes files into the controller's attachment state, which this
    // composer never reads but which `PromptInput` still base64-encodes on every submit — so letting the
    // event through would upload each file twice and stall the send behind the encode.
    event.stopPropagation();
    ChatUploadHelper.readDrop(
      event.dataTransfer,
      canUploadFolders || dataRoom.isAllowed
    )
      .then((dropped) =>
        // Folders are read here for the data room alone until folder upload is launched, so a dropped
        // folder never turns into ordinary attachments.
        !canUploadFolders && DataRoomUploadHelper.hasFolderFiles(dropped)
          ? dataRoom.startUpload(dropped)
          : ingestFiles(dropped)
      )
      .catch(() => toast.error('Could not read the dropped files.'));
  };

  // Always wired: when it wasn't, a dropped file fell through to `PromptInput`'s own listener, which
  // parks it in controller state this composer never reads — so the drop silently did nothing.
  const dropHandlers: Pick<
    DOMAttributes<HTMLDivElement>,
    'onDragOverCapture' | 'onDropCapture'
  > = {
    onDragOverCapture: onComposerDragOver,
    onDropCapture: onComposerDrop,
  };

  // With nothing to add, the "+" is not drawn at all rather than opening an empty menu.
  const addMenuItems = [
    ...(allowAttachments
      ? [
          {
            label: 'Add files',
            icon: Icons.Paperclip,
            onClick: () => fileInputRef.current?.click(),
          },
          ...(canUploadFolders
            ? [
                {
                  label: 'Add folder',
                  icon: Icons.Folder,
                  onClick: () => folderInputRef.current?.click(),
                },
              ]
            : []),
          ...(dataRoom.isAllowed
            ? [
                {
                  label: 'Upload a data room',
                  icon: Icons.UploadCloud01,
                  onClick: () => dataRoomFileInputRef.current?.click(),
                },
                {
                  label: 'Upload a data room folder',
                  icon: Icons.FolderPlus,
                  onClick: () => dataRoomFolderInputRef.current?.click(),
                },
              ]
            : []),
        ]
      : []),
    ...(isConnectorsLaunched
      ? [
          {
            label: 'Connectors',
            icon: Icons.Share07,
            onClick: () => setIsConnectorsModalOpen(true),
          },
        ]
      : []),
  ];

  const controllerRef = useRef<PromptInputControllerProps | null>(null);
  const isSubmittingRef = useRef(false);

  const handleSubmit = (message: PromptInputMessage) => {
    const text = message.text.trim();
    if (!text || disabled) return undefined;
    // Enter skips a disabled send, so this only backs up the button; rejecting keeps the text.
    if (text.length > COMMENDA_AI_MESSAGE_MAX_LENGTH)
      return Promise.reject(new Error(MESSAGE_TOO_LONG_ERROR));
    // Rejected rather than ignored: a resolved submit would clear the text the user is now typing.
    if (isSubmittingRef.current)
      return Promise.reject(new Error('A send is already in progress.'));
    const sentItems = attachments;
    const sentDataRoom = dataRoom.readyUpload;
    const submitted = onSubmit(
      text,
      sentItems.map((item) => item.data),
      sentDataRoom ?? undefined
    );
    // The room is dropped from the composer only once its message is sent; a failed send keeps it.
    const clearSentRoom = () => {
      if (sentDataRoom) dataRoom.clearAfterSend();
    };
    setAttachments((current) =>
      current.filter((item) => !sentItems.includes(item))
    );
    const revokeSent = () =>
      sentItems.forEach((item) => URL.revokeObjectURL(item.previewUrl));
    if (!(submitted instanceof Promise)) {
      revokeSent();
      clearSentRoom();
      return undefined;
    }
    isSubmittingRef.current = true;
    submitted
      .then(
        () => {
          revokeSent();
          clearSentRoom();
        },
        () => {
          const textInput = controllerRef.current?.textInput;
          if (textInput && !textInput.value.trim())
            textInput.setInput(message.text);
          setAttachments((current) => [
            ...sentItems.filter(
              (item) =>
                !current.some(
                  (existing) => existing.data.s3Key === item.data.s3Key
                )
            ),
            ...current,
          ]);
        }
      )
      .finally(() => {
        isSubmittingRef.current = false;
      });
    return undefined;
  };

  return (
    <div className="flex w-full flex-col gap-sm" {...dropHandlers}>
      <div
        className={cn(
          contextSlot &&
            'flex flex-col gap-sm rounded-b-3xl rounded-t-xl bg-bg-secondary pt-sm'
        )}
      >
        {contextSlot && <div className="px-lg">{contextSlot}</div>}
        <PromptInputProvider initialInput={initialInput}>
          <PromptInputControllerBridge controllerRef={controllerRef} />
          <PromptInput
            onSubmit={handleSubmit}
            className={cn(
              '[&_[data-slot=input-group]]:!rounded-[20px] [&_[data-slot=input-group]]:!border-[0.5px]',
              '[&_[data-slot=input-group]]:!border-border-tertiary [&_[data-slot=input-group]]:!bg-bg-primary',
              '[&_[data-slot=input-group]]:!shadow-[0px_2px_24px_0px_rgba(0,0,0,0.04)]',
              // Focus (Figma 61:21993): the border turns brand blue — no ring/glow. The ai-elements
              // default 1px near-black `ring-chat-ring` is suppressed via the ring-0 override below.
              '[&_[data-slot=input-group]]:transition-[border-color]',
              '[&_[data-slot=input-group]]:focus-within:!border-fg-brand',
              '[&_[data-slot=input-group]]:focus-within:!ring-0'
            )}
          >
            {attachments.length > 0 && (
              <PromptInputHeader>
                <AttachmentPillGroups
                  items={attachments.map(({ data, previewUrl }) => ({
                    key: data.s3Key,
                    fileName: data.fileName,
                    mimeType: data.mimeType,
                    fileRef: previewUrl,
                    onRemove: () => removeAttachment(data.s3Key),
                  }))}
                />
              </PromptInputHeader>
            )}
            {(dataRoom.progress ||
              dataRoom.isPreparing ||
              dataRoom.error ||
              dataRoom.resumeFileCount !== null) && (
              <PromptInputHeader>
                <DataRoomUploadProgress
                  progress={dataRoom.progress}
                  isPreparing={dataRoom.isPreparing}
                  error={dataRoom.error}
                  resumeFileCount={dataRoom.resumeFileCount}
                  onFilesChoose={() => dataRoomFileInputRef.current?.click()}
                  onFileRetry={dataRoom.retryFile}
                  onUploadCancel={dataRoom.cancelUpload}
                />
              </PromptInputHeader>
            )}
            <PromptInputBody>
              <PromptInputTextarea
                disabled={disabled}
                autoFocus={autoFocus}
                placeholder={placeholder}
                className="min-h-[53px] px-xl pb-none pt-xl"
              />
              <MessageLengthHint />
            </PromptInputBody>
            <PromptInputFooter className="gap-xl px-xl pb-xl pt-sm">
              <PromptInputTools>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={
                    dataRoom.isAllowed
                      ? DataRoomUploadHelper.acceptWithZip(ACCEPT)
                      : ACCEPT
                  }
                  multiple
                  className="hidden"
                  onChange={onFileChange}
                />
                {canUploadFolders && (
                  <input
                    ref={folderInputRef}
                    type="file"
                    // @ts-expect-error -- webkitdirectory is not in the HTML spec but supported by browsers
                    webkitdirectory=""
                    className="hidden"
                    onChange={onFileChange}
                  />
                )}
                {dataRoom.isAllowed && (
                  <>
                    <input
                      ref={dataRoomFileInputRef}
                      type="file"
                      multiple
                      className="hidden"
                      onChange={onDataRoomFileChange}
                    />
                    <input
                      ref={dataRoomFolderInputRef}
                      type="file"
                      // @ts-expect-error -- webkitdirectory is not in the HTML spec but supported by browsers
                      webkitdirectory=""
                      className="hidden"
                      onChange={onDataRoomFileChange}
                    />
                  </>
                )}
                {addMenuItems.length > 0 && (
                  <Dropdown
                    align="start"
                    side="top"
                    onOpenChange={setIsAddMenuOpen}
                    menu={{ groups: [{ items: addMenuItems }] }}
                  >
                    <span className="inline-flex">
                      <PromptInputButton
                        aria-label="Add files or connect apps"
                        className={cn(
                          'size-7 rounded-full text-fg-secondary hover:bg-bg-tertiary hover:text-text-primary',
                          isAddMenuOpen && 'bg-bg-active-dark text-text-primary'
                        )}
                      >
                        <Icons.Plus className="size-4" />
                      </PromptInputButton>
                    </span>
                  </Dropdown>
                )}
                {toolsSlot && (
                  <>
                    {/* Separates the add menu from the surface's own controls (Figma A-M 836:107925).
                      Rendered with the slot, so a composer without one is unchanged. */}
                    <span
                      aria-hidden="true"
                      className="mx-xs h-7 w-px shrink-0 bg-border-primary"
                    />
                    {toolsSlot}
                  </>
                )}
              </PromptInputTools>
              <div className="flex flex-1 items-center justify-end">
                <ChatSubmit
                  status={status}
                  onStop={onStop}
                  blocked={
                    uploadingCount > 0 || disabled || dataRoom.isUploading
                  }
                />
              </div>
            </PromptInputFooter>
          </PromptInput>
        </PromptInputProvider>
      </div>
      {renderConnectorsModal?.({
        isOpen: isConnectorsModalOpen,
        onClose: () => setIsConnectorsModalOpen(false),
      })}
      {showDisclaimer && (
        <p className="txt-extra-small-regular text-center text-text-tertiary">
          Chat is in beta.
        </p>
      )}
    </div>
  );
}
